import cloneDeep from 'lodash.clonedeep';
import {
  AccessFunctionFormData,
  ContentDeliveryVia,
  DataDeliveryVia,
  AccessFunctionInterfaceFormSectionType,
  POSSIBLE_CONTENT_DELIVERY_VIA,
  POSSIBLE_DATA_DELIVERY_VIA,
  AccessFunctionFormSection
} from 'data/accessFunction/accessFunction.types';
import { NodeData } from 'data/nodes/nodes.types';
import { FormTemplate, FormTemplateOption } from 'data/template/template.types';
import { createAccessFunctionBasedOnTemplate } from './accessFunctionTemplate.factory';

const POSSIBLE_INTERFACES: AccessFunctionInterfaceFormSectionType[] = [
  'provisioningInterfaces',
  'dataInterfaces',
  'contentInterfaces',
];

export function handleNodeNameFieldUpdate(
  data: Partial<AccessFunctionFormData>,
  value: string,
  formSection: AccessFunctionInterfaceFormSectionType | AccessFunctionFormSection,
  fieldName: string,
) {

  const clonedData = cloneDeep(data);

  if (formSection === 'general' && fieldName === 'contentDeliveryVia') {
    clonedData.mdf3NodeName = '';
  }

  if (formSection === 'dataInterfaces' && value === 'directFromAF') {
    clonedData.mdf2NodeName = '';
    if (clonedData.mdf2NodeNameMultiSelect) {
      clonedData.mdf2NodeNameMultiSelect = [];
    }
  } else if (formSection === 'contentInterfaces' && value === 'directFromAF') {
    clonedData.mdf3NodeName = '';
    if (clonedData.mdf3NodeNameMultiSelect) {
      clonedData.mdf3NodeNameMultiSelect = [];
    }
  }

  if (fieldName === 'mdf2NodeName') {
    if (formSection === 'dataInterfaces' || formSection === 'provisioningInterfaces') {
      clonedData.mdf2NodeName = value;
    }
  } else if (fieldName === 'mdf3NodeName') {
    if (formSection === 'contentInterfaces' || formSection === 'provisioningInterfaces') {
      clonedData.mdf3NodeName = value;
    }
  }

  if (fieldName === 'mdf2NodeNameMultiSelect') {
    if (formSection === 'dataInterfaces' || formSection === 'contentInterfaces') {
      clonedData.mdf2NodeNameMultiSelect = ((value as unknown) as FormTemplateOption[]);
    }
  }

  if (fieldName === 'mdf3NodeNameMultiSelect') {
    if (formSection === 'dataInterfaces' || formSection === 'contentInterfaces') {
      clonedData.mdf3NodeNameMultiSelect = ((value as unknown) as FormTemplateOption[]);
    }
  }
  return clonedData;
}

/**
 * @returns the updated AccessFunctionFormData with the relevent interface section changed based
 * on the contentDeliveryVia field change at the general section
 */

export function handleDeliveryViaFieldUpdate(
  template: FormTemplate | undefined,
  data: Partial<AccessFunctionFormData>,
  deliveryVia: ContentDeliveryVia | DataDeliveryVia,
): Partial<AccessFunctionFormData> {
  if (template == null || data.dataDeliveryVia === deliveryVia) {
    return data;
  }

  const clonedData = cloneDeep(data);

  const affectedInterfaces = getAffectedInterfacesByDeliveryViaChange(template, deliveryVia) ?? [];

  affectedInterfaces?.forEach((interfaceName) => {
    if (interfaceName === 'provisioningInterfaces' || interfaceName === 'contentInterfaces' || interfaceName === 'dataInterfaces') {
      let forceInterfaceCreation = false;

      const interfaceData = clonedData[interfaceName];
      if (interfaceData != null && interfaceData.length > 0) {
        forceInterfaceCreation = true;
      }

      const initialData = createAccessFunctionBasedOnTemplate(template, forceInterfaceCreation);
      clonedData[interfaceName] = initialData[interfaceName];
    }
  });

  return clonedData;
}

export function adaptInitialDataForSS8DistributorOption(
  template: FormTemplate | undefined,
  data: Partial<AccessFunctionFormData>,
  deliveryViaType: ContentDeliveryVia | DataDeliveryVia | undefined,
): Partial<AccessFunctionFormData> {
  if (template == null || !deliveryViaType) {
    return data;
  }

  const clonedData = cloneDeep(data);

  const affectedInterfaces = getAffectedInterfacesByDeliveryViaChange(template, deliveryViaType) ?? [];

  affectedInterfaces?.forEach((interfaceName) => {
    if (interfaceName === 'provisioningInterfaces' || interfaceName === 'contentInterfaces' || interfaceName === 'dataInterfaces') {
      let forceInterfaceCreation = false;

      const interfaceData = clonedData[interfaceName];
      if (interfaceData != null && interfaceData.length > 0) {
        forceInterfaceCreation = true;
      }

      const initialData = createAccessFunctionBasedOnTemplate(template, forceInterfaceCreation);
      clonedData[interfaceName] = initialData[interfaceName];
    }
  });

  return clonedData;
}

function getAffectedInterfacesByDeliveryViaChange(
  template: FormTemplate | undefined,
  deliveryVia: DataDeliveryVia | ContentDeliveryVia | undefined,
): AccessFunctionInterfaceFormSectionType[] | undefined {
  if (template == null || !deliveryVia) {
    return;
  }

  const affectedInterfaces = POSSIBLE_INTERFACES.filter((interfaceName) => {
    return template[`${interfaceName}_${deliveryVia}`] != null;
  });

  return affectedInterfaces;
}

/**
 * @returns the updated contentDeliveryVia and dataDeliveryVia when the 'Deliver via' changes at
 * the dataInterfaces or contentInterfaces section
 */
export function handleInterfacesSectionDeliveryViaFieldUpdate(
  template: FormTemplate | undefined,
  interfaceName: AccessFunctionInterfaceFormSectionType,
  clonedData: Partial<AccessFunctionFormData>,
  fieldName: string,
  fieldValue: unknown,
): Partial<AccessFunctionFormData> {

  // This function is only for fields, contentDeliveryVia and dataDeliveryVia
  if (template == null || (fieldName !== 'contentDeliveryVia' && fieldName !== 'dataDeliveryVia')) {
    return clonedData;
  }

  // no change to contentDeliveryVia field value
  if (fieldName === 'contentDeliveryVia' && clonedData.contentDeliveryVia === fieldValue) {
    return clonedData;
  }
  if (fieldName === 'dataDeliveryVia' && clonedData.dataDeliveryVia === fieldValue) {
    return clonedData;
  }

  // unrecognized contentDeliveryVia value
  if (fieldName === 'contentDeliveryVia' && POSSIBLE_CONTENT_DELIVERY_VIA.includes(fieldValue as string) === false) {
    return clonedData;
  }
  if (fieldName === 'dataDeliveryVia' && POSSIBLE_DATA_DELIVERY_VIA.includes(fieldValue as string) === false) {
    return clonedData;
  }

  if (fieldName === 'contentDeliveryVia') {
    clonedData.contentDeliveryVia = fieldValue as ContentDeliveryVia;
  } else if (fieldName === 'dataDeliveryVia') {
    clonedData.dataDeliveryVia = fieldValue as DataDeliveryVia;
  }

  return clonedData;
}

export function handleCallContentDestinationIPFieldUpdate(
  template: FormTemplate | undefined,
  interfaceName: AccessFunctionInterfaceFormSectionType,
  clonedData: Partial<AccessFunctionFormData>,
  fieldName: string,
  fieldValue: unknown,
  xcipioNodes: NodeData[],
): Partial<AccessFunctionFormData> {
  if (template == null || interfaceName !== 'provisioningInterfaces') {
    return clonedData;
  }

  if (fieldName === 'mdf3NodeName') {
    const node = xcipioNodes?.find((node) => node.nodeName === fieldValue);

    if (node) {
      const ipV4 = node.ipAddress;

      if (clonedData.provisioningInterfaces?.length) {
        clonedData.provisioningInterfaces[0].ccdestip = ipV4;
      }
    }
  }

  if (fieldName === 'mdf3NodeNameMultiSelect') {
    if (clonedData?.provisioningInterfaces) {
      const fieldValueOptions = fieldValue as FormTemplateOption[] | undefined;
      clonedData.provisioningInterfaces.forEach((provisioningInterface, pi) => {
        const nodeName = fieldValueOptions?.[pi]?.value;
        if (nodeName == null) {
          return;
        }

        const node = xcipioNodes?.find((node) => node.nodeName === nodeName);

        if (node) {
          const ipV4 = node.ipAddress;
          provisioningInterface.ccdestip = ipV4;
        }
      });
    }
  }

  return clonedData;
}
